import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Trash, DownloadSimple, Plus, Phone, Globe, ArrowSquareOut } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { api } from "../../lib/api";
import { STATUSES, OUTCOMES, CARRIERS, trackingUrl, errMsg, downloadBlob, webUrl } from "./savShared";
import { SupplierDialog } from "./SavSuppliers";

const EMPTY = { product_name: "", product_ref: "", serial_number: "", quantity: 1, reason: "", supplier_id: "", rma_number: "",
  return_carrier: "", tracking_number: "", return_url: "", status: "waiting_return", outcome: "", replacement_tracking: "",
  refund_amount: "", refund_date: "", notes: "" };

const sel = "block w-full rounded-md border bg-background px-3 py-2 text-sm";

function F({ label, children, className = "" }) {
  return <div className={className}><Label className="text-xs">{label}</Label>{children}</div>;
}

function SupplierBlock({ f, set, suppliers, reloadSuppliers }) {
  const [adding, setAdding] = useState(false);
  const s = suppliers.find((x) => x.id === f.supplier_id);
  return (
    <div className="space-y-2">
      <F label="Fournisseur">
        <div className="flex gap-2">
          <select className={sel} value={f.supplier_id} onChange={(e) => set({ supplier_id: e.target.value })} data-testid="sav-supplier-select">
            <option value="">— Choisir —</option>
            {suppliers.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          <Button type="button" variant="outline" size="icon" onClick={() => setAdding(true)} title="Nouveau fournisseur" data-testid="sav-supplier-add"><Plus size={14} /></Button>
        </div>
      </F>
      {s && (s.phone || s.website || s.email) && (
        <div className="flex flex-wrap gap-3 rounded-md bg-muted px-3 py-2 text-xs" data-testid="sav-supplier-info">
          {s.phone && <a href={`tel:${s.phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone size={12} />{s.phone}</a>}
          {s.website && <a href={webUrl(s.website)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline"><Globe size={12} />{s.website}</a>}
          {s.email && <a href={`mailto:${s.email}`} className="hover:underline">{s.email}</a>}
        </div>
      )}
      <SupplierDialog open={adding} onClose={() => setAdding(false)} onSaved={(n) => { reloadSuppliers(); set({ supplier_id: n.id }); }} />
    </div>
  );
}

function Attachments({ c, onChange }) {
  const [busy, setBusy] = useState(false);
  const up = async (files) => {
    setBusy(true);
    for (const file of Array.from(files)) {
      const fd = new FormData(); fd.append("file", file);
      try { await api.post(`/sav/cases/${c.id}/attachments`, fd); } catch (e) { toast.error(`${file.name} : ${errMsg(e)}`); }
    }
    setBusy(false); onChange();
  };
  const del = async (a) => {
    if (!window.confirm(`Supprimer ${a.filename} ?`)) return;
    try { await api.delete(`/sav/cases/${c.id}/attachments/${a.id}`); } catch (e) { toast.error(errMsg(e)); }
    onChange();
  };
  return (
    <div className="space-y-2" data-testid="sav-attachments">
      {(c.attachments || []).map((a) => (
        <div key={a.id} className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm">
          <Paperclip size={14} /><span className="flex-1 truncate">{a.filename}</span>
          <span className="text-[11px] text-muted-foreground">{Math.ceil(a.size / 1024)} Ko</span>
          <button type="button" onClick={() => downloadBlob(`/sav/cases/${c.id}/attachments/${a.id}`, a.filename)} data-testid={`sav-att-download-${a.id}`}><DownloadSimple size={14} /></button>
          <button type="button" className="text-red-600" onClick={() => del(a)} data-testid={`sav-att-delete-${a.id}`}><Trash size={14} /></button>
        </div>
      ))}
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-dashed px-3 py-1.5 text-xs hover:bg-muted">
        <Paperclip size={14} />{busy ? "Envoi…" : "Ajouter bon de retour, étiquette, photo, facture… (15 Mo max)"}
        <input type="file" multiple className="hidden" disabled={busy} onChange={(e) => { up(e.target.files); e.target.value = ""; }} data-testid="sav-att-input" />
      </label>
    </div>
  );
}

function History({ items }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-1" data-testid="sav-history">
      {[...items].reverse().map((h, i) => (
        <div key={i} className="text-xs"><span className="text-muted-foreground">{new Date(h.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · {h.by}</span> — <b>{h.action}</b>{h.detail ? ` : ${h.detail}` : ""}</div>
      ))}
    </div>
  );
}

export function SavCaseDialog({ open, caseId, onClose, onSaved, suppliers, reloadSuppliers }) {
  const [f, setF] = useState(EMPTY);
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((prev) => ({ ...prev, ...p }));
  const loadCase = (id) => api.get(`/sav/cases/${id}`).then((r) => {
    setC(r.data);
    setF(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, r.data[k] ?? EMPTY[k]])));
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    if (!open) { return; }
    if (caseId) { loadCase(caseId); } else { setC(null); setF(EMPTY); }
  }, [open, caseId]); // eslint-disable-line

  const save = async () => {
    if (!f.product_name.trim()) { toast.error("Indiquez le produit"); return; }
    setBusy(true);
    const body = { ...f, supplier_id: f.supplier_id || null, outcome: f.outcome || null, quantity: Number(f.quantity) || 1,
      refund_amount: f.refund_amount === "" || f.refund_amount === null ? null : Number(String(f.refund_amount).replace(",", ".")),
      refund_date: f.refund_date || null };
    try {
      const r = c ? await api.put(`/sav/cases/${c.id}`, body) : await api.post("/sav/cases", body);
      toast.success(c ? "Dossier mis à jour" : `Dossier ${r.data.number} créé`);
      onSaved?.();
      if (c) { onClose(); } else { setC(r.data); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const tUrl = trackingUrl(f.return_carrier, f.tracking_number);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="sav-case-dialog">
        <DialogHeader>
          <DialogTitle>{c ? `Dossier ${c.number}` : "Nouveau dossier SAV"}</DialogTitle>
          <DialogDescription>{c ? `Créé le ${new Date(c.created_at).toLocaleDateString("fr-FR")} par ${c.created_by_name}` : "Retour d'un produit auprès du fournisseur."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <section className="grid gap-3 sm:grid-cols-4">
            <F label="Produit *" className="sm:col-span-2"><Input value={f.product_name} onChange={(e) => set({ product_name: e.target.value })} data-testid="sav-product-name" /></F>
            <F label="Référence"><Input value={f.product_ref} onChange={(e) => set({ product_ref: e.target.value })} data-testid="sav-product-ref" /></F>
            <F label="Quantité"><Input type="number" min={1} value={f.quantity} onChange={(e) => set({ quantity: e.target.value })} data-testid="sav-quantity" /></F>
            <F label="N° de série" className="sm:col-span-2"><Input value={f.serial_number} onChange={(e) => set({ serial_number: e.target.value })} data-testid="sav-serial" /></F>
            <F label="Statut" className="sm:col-span-2">
              <select className={sel} value={f.status} onChange={(e) => set({ status: e.target.value })} data-testid="sav-status-select">
                {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </F>
            <F label="Motif du retour" className="sm:col-span-4"><Textarea rows={2} value={f.reason} onChange={(e) => set({ reason: e.target.value })} data-testid="sav-reason" /></F>
          </section>
          <section className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><SupplierBlock f={f} set={set} suppliers={suppliers} reloadSuppliers={reloadSuppliers} /></div>
            <F label="N° RMA"><Input value={f.rma_number} onChange={(e) => set({ rma_number: e.target.value })} data-testid="sav-rma" /></F>
            <F label="Site lié au retour (portail RMA, suivi…)"><Input value={f.return_url} onChange={(e) => set({ return_url: e.target.value })} placeholder="https://" data-testid="sav-return-url" /></F>
            <F label="Transporteur du retour">
              <select className={sel} value={f.return_carrier} onChange={(e) => set({ return_carrier: e.target.value })} data-testid="sav-carrier">
                <option value="">—</option>{Object.keys(CARRIERS).map((k) => <option key={k}>{k}</option>)}<option>Autre</option>
              </select>
            </F>
            <F label="N° de suivi retour">
              <div className="flex gap-2">
                <Input value={f.tracking_number} onChange={(e) => set({ tracking_number: e.target.value })} data-testid="sav-tracking" />
                {tUrl && <a href={tUrl} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-md border px-2" title="Suivre le colis" data-testid="sav-tracking-link"><ArrowSquareOut size={14} /></a>}
              </div>
            </F>
          </section>
          <section className="grid gap-3 sm:grid-cols-3 rounded-lg border p-3">
            <F label="Issue du retour">
              <select className={sel} value={f.outcome || ""} onChange={(e) => set({ outcome: e.target.value })} data-testid="sav-outcome">
                <option value="">Pas encore connue</option>{Object.entries(OUTCOMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
            {f.outcome === "exchanged" && <F label="N° de suivi du produit échangé" className="sm:col-span-2"><Input value={f.replacement_tracking} onChange={(e) => set({ replacement_tracking: e.target.value })} data-testid="sav-replacement-tracking" /></F>}
            {f.outcome === "refunded" && <>
              <F label="Montant remboursé (€)"><Input inputMode="decimal" value={f.refund_amount ?? ""} onChange={(e) => set({ refund_amount: e.target.value })} data-testid="sav-refund-amount" /></F>
              <F label="Date du remboursement"><Input type="date" value={f.refund_date || ""} onChange={(e) => set({ refund_date: e.target.value })} data-testid="sav-refund-date" /></F>
            </>}
          </section>
          <F label="Notes"><Textarea rows={3} value={f.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="sav-notes" /></F>
          {c ? <F label="Pièces jointes"><Attachments c={c} onChange={() => loadCase(c.id)} /></F>
            : <p className="text-xs text-muted-foreground">Les pièces jointes pourront être ajoutées dès la création du dossier.</p>}
          {c && <F label="Historique"><History items={c.history} /></F>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="sav-cancel">Fermer</Button>
          <Button onClick={save} disabled={busy} data-testid="sav-save">{busy ? "…" : c ? "Enregistrer" : "Créer le dossier"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
